import { FormEvent, useEffect, useMemo, useState, type InputHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import AuthDateOfBirthField from './AuthDateOfBirthField';
import AuthPasswordInput from './AuthPasswordInput';
import CountryDialCodeSelect from './CountryDialCodeSelect';
import GoogleAuthButton, { isGoogleAuthConfigured } from './GoogleAuthButton';
import { authService } from '../../services/authService';
import {
  CATEGORIE_PARTICIPANT,
  getCategorieLabel,
  resolveCategorieParticipant,
  isValidBirthDateInput,
} from '../../utils/categorieParticipant';
import { normalizePhoneInput } from '../../utils/authPhone';

const REGISTER_STEPS = 3;

type AuthMode = 'login' | 'register';

type Props = {
  mode: AuthMode;
  onModeChange: (mode: AuthMode) => void;
  onSuccess: (nextState: unknown) => void;
  initialCodeParrain?: string;
};

const loginSchema = z.object({
  email: z.string().min(1, 'Adresse email requise.').email('Adresse email invalide.'),
  motdepasse: z.string().min(6, 'Mot de passe requis (minimum 6 caractères).'),
});

const registerSchema = z
  .object({
    prenom: z.string().min(2, 'Prénom requis.'),
    nom: z.string().min(2, 'Nom requis.'),
    postnom: z.string().optional(),
    email: z.string().min(1, 'Adresse email requise.').email('Adresse email invalide.'),
    telephone: z.string().min(8, 'Téléphone requis.'),
    dateNaissance: z.string().min(1, 'Date de naissance requise.'),
    codeParrain: z.string().optional(),
    motdepasse: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères.'),
    confirmMotdepasse: z.string().min(8, 'Confirmation du mot de passe requise.'),
    acceptationConditions: z.literal(true, {
      errorMap: () => ({ message: "Vous devez accepter les conditions d'utilisation." }),
    }),
  })
  .refine((data) => data.motdepasse === data.confirmMotdepasse, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['confirmMotdepasse'],
  });

function inferMessage(error: unknown): string {
  if (error instanceof z.ZodError) {
    return error.errors[0]?.message ?? 'Données invalides.';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Une erreur est survenue.';
}

const authIconInputClass =
  'w-full rounded-lg border-none bg-surface-container-high py-2 pl-10 pr-3 text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-0 sm:py-2.5 sm:pl-11';

type AuthIconInputProps = {
  label: string;
  icon: string;
  className?: string;
} & InputHTMLAttributes<HTMLInputElement>;

function AuthIconInput({ label, icon, className = '', ...inputProps }: AuthIconInputProps) {
  return (
    <label className={`block space-y-1 ${className}`}>
      <span className="text-xs font-bold text-on-surface-variant">{label}</span>
      <div className="relative rounded-lg border border-transparent focus-glow">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          <LucideSymbolIcon name={icon} tone="muted" size={18} />
        </span>
        <input {...inputProps} className={authIconInputClass} />
      </div>
    </label>
  );
}

export default function AcademiaAuthForm({ mode, onModeChange, onSuccess, initialCodeParrain = '' }: Props) {
  const isRegister = mode === 'register';
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleAuthEnabled = isGoogleAuthConfigured();
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [otpPendingEmail, setOtpPendingEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpResending, setOtpResending] = useState(false);
  const [registerStep, setRegisterStep] = useState<1 | 2 | 3>(1);
  const [dialCode, setDialCode] = useState('+243');

  const [loginValues, setLoginValues] = useState({ email: '', motdepasse: '' });
  const [registerValues, setRegisterValues] = useState({
    prenom: '',
    nom: '',
    postnom: '',
    email: '',
    telephone: '',
    dateNaissance: '',
    codeParrain: '',
    motdepasse: '',
    confirmMotdepasse: '',
    acceptationConditions: false,
    isFonctionnaire: false,
    profession: '',
  });

  const hasOtpPending = Boolean(otpPendingEmail);

  const categoriePreview = useMemo(() => {
    if (!isRegister || hasOtpPending) return null;
    return resolveCategorieParticipant({
      dateNaissance: registerValues.dateNaissance,
      isFonctionnaire: registerValues.isFonctionnaire,
    });
  }, [
    isRegister,
    hasOtpPending,
    registerValues.dateNaissance,
    registerValues.isFonctionnaire,
  ]);

  const categoriePreviewLabel = getCategorieLabel(categoriePreview);
  const ageIneligible =
    isRegister &&
    isValidBirthDateInput(registerValues.dateNaissance) &&
    !registerValues.isFonctionnaire &&
    categoriePreview === null;

  const step1Complete = useMemo(() => {
    return Boolean(
      registerValues.prenom.trim() &&
        registerValues.nom.trim() &&
        isValidBirthDateInput(registerValues.dateNaissance),
    );
  }, [registerValues.prenom, registerValues.nom, registerValues.dateNaissance]);

  const step2Complete = useMemo(() => {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerValues.email.trim());
    const phoneOk = Boolean(normalizePhoneInput(registerValues.telephone, dialCode));
    const ageOk =
      registerValues.isFonctionnaire ||
      Boolean(resolveCategorieParticipant({ dateNaissance: registerValues.dateNaissance }));
    return Boolean(emailOk && phoneOk && ageOk);
  }, [
    dialCode,
    registerValues.dateNaissance,
    registerValues.email,
    registerValues.isFonctionnaire,
    registerValues.telephone,
  ]);

  const step2Hint = useMemo(() => {
    if (registerStep !== 2 || step2Complete) return null;
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registerValues.email.trim());
    const phoneOk = Boolean(normalizePhoneInput(registerValues.telephone, dialCode));
    const ageOk =
      registerValues.isFonctionnaire ||
      Boolean(resolveCategorieParticipant({ dateNaissance: registerValues.dateNaissance }));
    if (!emailOk) return 'Saisissez une adresse e-mail valide.';
    if (!phoneOk) return 'Saisissez un numéro de téléphone valide (indicatif + numéro).';
    if (!ageOk) {
      return 'Âge minimum 17 ans, ou cochez « fonctionnaire / employé » ci-dessus si applicable.';
    }
    return null;
  }, [
    dialCode,
    registerStep,
    registerValues.dateNaissance,
    registerValues.email,
    registerValues.isFonctionnaire,
    registerValues.telephone,
    step2Complete,
  ]);

  const ageEligible = registerValues.isFonctionnaire || Boolean(categoriePreview);

  const canSubmitRegister = useMemo(
    () =>
      step1Complete &&
      step2Complete &&
      registerValues.motdepasse.length >= 8 &&
      registerValues.motdepasse === registerValues.confirmMotdepasse &&
      registerValues.acceptationConditions &&
      ageEligible,
    [registerValues, step1Complete, step2Complete, ageEligible],
  );

  useEffect(() => {
    if (mode !== 'register') {
      setRegisterStep(1);
    }
  }, [mode]);

  useEffect(() => {
    const code = initialCodeParrain.trim().toUpperCase();
    if (!code || mode !== 'register') return;
    setRegisterValues((current) =>
      current.codeParrain === code ? current : { ...current, codeParrain: code },
    );
  }, [initialCodeParrain, mode]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setLoading(true);

    try {
      if (hasOtpPending) {
        const trimmedEmail = otpPendingEmail.trim();
        const trimmedOtp = otpCode.trim();
        if (!trimmedEmail || trimmedOtp.length !== 6) {
          throw new Error('Entrez le code OTP à 6 chiffres envoyé par email.');
        }
        const authState = await authService.verifyOtp({ email: trimmedEmail, otp: trimmedOtp });
        setSuccessMessage('Compte validé. Connexion réussie.');
        onSuccess(authState);
        return;
      }

      if (!isRegister) {
        const parsed = loginSchema.parse(loginValues);
        const authState = await authService.login({
          email: parsed.email.trim(),
          motdepasse: parsed.motdepasse,
        });
        setSuccessMessage('Connexion réussie.');
        onSuccess(authState);
        return;
      }

      if (registerStep !== 3) {
        throw new Error('Complétez l’étape 3 avant de valider l’inscription.');
      }

      const telephone = normalizePhoneInput(registerValues.telephone, dialCode);
      if (!telephone) {
        throw new Error('Numéro de téléphone invalide.');
      }

      const parsed = registerSchema.parse({
        ...registerValues,
        prenom: registerValues.prenom.trim(),
        nom: registerValues.nom.trim(),
        postnom: registerValues.postnom.trim() || undefined,
        email: registerValues.email.trim(),
        telephone,
        codeParrain: registerValues.codeParrain.trim() || undefined,
      });

      if (
        !registerValues.isFonctionnaire &&
        !resolveCategorieParticipant({ dateNaissance: parsed.dateNaissance })
      ) {
        throw new Error('Âge minimum : 17 ans (catégorie jeunesse) ou cochez fonctionnaire / employé.');
      }

      const result = await authService.register({
        email: parsed.email,
        telephone: parsed.telephone,
        motdepasse: parsed.motdepasse,
        confirmMotdepasse: parsed.confirmMotdepasse,
        role: 'APPRENANT',
        prenom: parsed.prenom,
        nom: parsed.nom,
        postnom: parsed.postnom,
        dateNaissance: parsed.dateNaissance,
        ville: 'Kinshasa',
        commune: '—',
        quartier: '—',
        detailsRue: '—',
        typeCompte: 'STANDARD',
        acceptationConditions: true,
        codeParrain: parsed.codeParrain,
        ...(registerValues.isFonctionnaire
          ? { categorieManuelle: CATEGORIE_PARTICIPANT.FONCTIONNAIRE_EMPLOYE }
          : {}),
        profession: registerValues.profession.trim() || undefined,
      });

      if (result?.otpRequired && result.email) {
        setOtpPendingEmail(result.email);
        setSuccessMessage(result.message || 'Code OTP envoyé par email.');
        return;
      }

      setSuccessMessage('Inscription réussie.');
      onSuccess(result);
    } catch (error) {
      setErrorMessage(inferMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setErrorMessage('');
    setSuccessMessage('');
    setGoogleLoading(true);
    try {
      const authState = await authService.loginWithGoogle(idToken);
      setSuccessMessage(
        authState.isNewUser ? 'Compte créé avec Google. Bienvenue !' : 'Connexion Google réussie.',
      );
      onSuccess(authState);
    } catch (error) {
      setErrorMessage(inferMessage(error));
    } finally {
      setGoogleLoading(false);
    }
  };

  const resendOtp = async () => {
    if (!otpPendingEmail.trim()) return;
    try {
      setOtpResending(true);
      setErrorMessage('');
      await authService.requestOtp({ email: otpPendingEmail.trim() });
      setSuccessMessage('Un nouveau code OTP a été envoyé par email.');
    } catch (error) {
      setErrorMessage(inferMessage(error));
    } finally {
      setOtpResending(false);
    }
  };

  return (
    <>
      <div className="mb-3 flex rounded-public-btn bg-surface-container-high p-0.5">
        <button
          type="button"
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-public-btn py-1.5 text-xs font-semibold transition-all sm:gap-2 sm:text-sm ${
            !isRegister
              ? 'bg-surface-container-lowest text-primary shadow-sm'
              : 'text-on-surface-variant hover:text-on-surface'
          }`}
          onClick={() => onModeChange('login')}
        >
          <LucideSymbolIcon name="login" tone="inherit" size={16} className="shrink-0 sm:h-[18px] sm:w-[18px]" />
          Se connecter
        </button>
        <button
          type="button"
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-public-btn py-1.5 text-xs font-semibold transition-all sm:gap-2 sm:text-sm ${
            isRegister
              ? 'bg-surface-container-lowest text-primary shadow-sm'
              : 'text-on-surface-variant hover:text-on-surface'
          }`}
          onClick={() => onModeChange('register')}
        >
          <LucideSymbolIcon name="person_add" tone="inherit" size={16} className="shrink-0 sm:h-[18px] sm:w-[18px]" />
          Créer un compte
        </button>
      </div>

      {googleAuthEnabled && !hasOtpPending ? (
        <div className="mb-3 space-y-2">
          <GoogleAuthButton
            mode={isRegister ? 'register' : 'login'}
            disabled={loading || googleLoading}
            onCredential={(token) => void handleGoogleCredential(token)}
            onError={(message) => setErrorMessage(message)}
          />
          <div className="flex items-center gap-2">
            <span className="h-px flex-1 bg-outline-variant/30" aria-hidden />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-on-surface-variant sm:text-xs">ou par email</span>
            <span className="h-px flex-1 bg-outline-variant/30" aria-hidden />
          </div>
        </div>
      ) : null}

      <form className="space-y-3" onSubmit={onSubmit} noValidate>
        {errorMessage ? (
          <div className="rounded-lg border border-error/30 bg-error-container px-3 py-2 text-xs text-on-error-container sm:text-sm">
            {errorMessage}
          </div>
        ) : null}
        {successMessage ? (
          <div className="rounded-lg border border-tertiary-fixed-dim/40 bg-tertiary-fixed/20 px-3 py-2 text-xs text-tertiary-container sm:text-sm">
            {successMessage}
          </div>
        ) : null}

        {hasOtpPending ? (
          <div className="space-y-2">
            <p className="text-xs text-on-surface-variant sm:text-sm">
              Code envoyé à <strong>{otpPendingEmail}</strong>.
            </p>
            <label className="block space-y-1">
              <span className="text-xs font-bold text-on-surface-variant">Code OTP (6 chiffres)</span>
              <div className="relative rounded-lg border border-transparent focus-glow">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                  <LucideSymbolIcon name="verified_user" tone="muted" size={18} />
                </span>
                <input
                  className={authIconInputClass}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                />
              </div>
            </label>
            <button
              type="button"
              className="w-full rounded-public-btn border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/5 disabled:opacity-60 sm:text-sm"
              disabled={otpResending}
              onClick={() => void resendOtp()}
            >
              {otpResending ? 'Renvoi en cours…' : 'Renvoyer le code'}
            </button>
          </div>
        ) : !isRegister ? (
          <div className="mt-2 space-y-3 sm:mt-3">
            <label className="block space-y-1">
              <span className="text-xs font-bold text-on-surface-variant">Adresse email</span>
              <div className="relative rounded-lg border border-transparent focus-glow">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                  <LucideSymbolIcon name="mail" tone="muted" size={18} />
                </span>
                <input
                  type="email"
                  autoComplete="email"
                  className="w-full rounded-lg border-none bg-surface-container-high py-2 pl-10 pr-3 text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-0 sm:py-2.5 sm:pl-11"
                  value={loginValues.email}
                  onChange={(e) => setLoginValues((c) => ({ ...c, email: e.target.value }))}
                  placeholder="vous@exemple.cd"
                />
              </div>
            </label>
            <AuthPasswordInput
              label="Mot de passe"
              autoComplete="current-password"
              value={loginValues.motdepasse}
              onChange={(e) => setLoginValues((c) => ({ ...c, motdepasse: e.target.value }))}
              placeholder="Votre mot de passe"
            />
            <p className="text-right text-xs sm:text-sm">
              <Link to="/forgot-password" className="font-semibold text-primary hover:underline">
                Mot de passe oublié ?
              </Link>
            </p>
          </div>
        ) : (
          <>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-[11px] font-bold text-on-surface-variant sm:text-xs">
                Étape {registerStep} sur {REGISTER_STEPS}
              </p>
              <div className="flex items-center gap-1" aria-hidden>
                {Array.from({ length: REGISTER_STEPS }, (_, i) => (
                  <span
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${
                      i + 1 === registerStep ? 'w-4 bg-primary' : 'w-1.5 bg-primary/25'
                    }`}
                  />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5">
              {registerStep === 1 ? (
                <>
                  <AuthIconInput
                    label="Prénom *"
                    icon="person"
                    value={registerValues.prenom}
                    onChange={(e) => setRegisterValues((c) => ({ ...c, prenom: e.target.value }))}
                    placeholder="Jean"
                    autoComplete="given-name"
                  />
                  <AuthIconInput
                    label="Nom *"
                    icon="assignment_ind"
                    value={registerValues.nom}
                    onChange={(e) => setRegisterValues((c) => ({ ...c, nom: e.target.value }))}
                    placeholder="Mbala"
                    autoComplete="family-name"
                  />
                  <AuthIconInput
                    label="Post-nom"
                    icon="badge"
                    className="sm:col-span-2"
                    value={registerValues.postnom}
                    onChange={(e) => setRegisterValues((c) => ({ ...c, postnom: e.target.value }))}
                    placeholder="Optionnel"
                  />
                  <AuthDateOfBirthField
                    label="Date de naissance"
                    value={registerValues.dateNaissance}
                    onChange={(dateNaissance) => setRegisterValues((c) => ({ ...c, dateNaissance }))}
                    required
                  />
                  {categoriePreviewLabel ? (
                    <div
                      className="sm:col-span-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-on-surface"
                      role="status"
                    >
                      <span className="font-bold uppercase tracking-wide text-primary">Catégorie · </span>
                      <span className="font-semibold">{categoriePreviewLabel}</span>
                    </div>
                  ) : null}
                  {ageIneligible ? (
                    <p className="sm:col-span-2 text-xs font-semibold text-error" role="alert">
                      Âge minimum 17 ans, ou cochez fonctionnaire / employé à l&apos;étape 2 si applicable.
                    </p>
                  ) : null}
                </>
              ) : registerStep === 2 ? (
                <>
                  {initialCodeParrain.trim() ? (
                    <div
                      className="sm:col-span-2 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2 text-xs text-on-surface"
                      role="status"
                    >
                      <span className="font-bold text-primary">Parrainage · </span>
                      code{' '}
                      <span className="font-bold text-slate-900">{initialCodeParrain.trim().toUpperCase()}</span>{' '}
                      appliqué automatiquement
                    </div>
                  ) : null}
                  <AuthIconInput
                    label="Adresse e-mail *"
                    icon="mail"
                    className="sm:col-span-2"
                    type="email"
                    autoComplete="email"
                    value={registerValues.email}
                    onChange={(e) => setRegisterValues((c) => ({ ...c, email: e.target.value }))}
                    placeholder="vous@exemple.com"
                  />
                  <div className="space-y-1 sm:col-span-2">
                    <span className="text-xs font-bold text-on-surface-variant">Téléphone *</span>
                    <div className="focus-glow flex rounded-lg border border-transparent bg-surface-container-high">
                      <span className="pointer-events-none flex shrink-0 items-center pl-3" aria-hidden>
                        <LucideSymbolIcon name="phone" tone="muted" size={18} />
                      </span>
                      <CountryDialCodeSelect embedded value={dialCode} onChange={setDialCode} />
                      <span className="my-2 w-px shrink-0 bg-outline-variant/30" aria-hidden />
                      <input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        className="min-w-0 flex-1 border-none bg-transparent py-2 pl-1 pr-3 text-sm text-on-surface placeholder:text-outline focus:outline-none focus:ring-0 sm:py-2.5"
                        value={registerValues.telephone}
                        onChange={(e) => setRegisterValues((c) => ({ ...c, telephone: e.target.value }))}
                        placeholder="97 000 00 00"
                      />
                    </div>
                  </div>
                  <label className="mt-2 flex items-start gap-2 sm:col-span-2 sm:mt-2.5">
                    <input
                      type="checkbox"
                      className="academia-checkbox mt-0.5"
                      checked={registerValues.isFonctionnaire}
                      onChange={(e) =>
                        setRegisterValues((c) => ({ ...c, isFonctionnaire: e.target.checked }))
                      }
                    />
                    <span className="text-xs text-on-surface-variant sm:text-sm">
                      Fonctionnaire ou employé (tarif dédié)
                    </span>
                  </label>
                  {registerValues.isFonctionnaire ? (
                    <AuthIconInput
                      label="Profession / employeur"
                      icon="work"
                      className="sm:col-span-2"
                      value={registerValues.profession}
                      onChange={(e) => setRegisterValues((c) => ({ ...c, profession: e.target.value }))}
                      placeholder="Ex. Agent administratif — Ministère X"
                    />
                  ) : null}
                  {categoriePreviewLabel ? (
                    <div
                      className="sm:col-span-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-on-surface"
                      role="status"
                    >
                      <span className="font-bold uppercase tracking-wide text-primary">Catégorie · </span>
                      <span className="font-semibold">{categoriePreviewLabel}</span>
                    </div>
                  ) : null}
                  {registerValues.dateNaissance.trim() &&
                  !registerValues.isFonctionnaire &&
                  !resolveCategorieParticipant({ dateNaissance: registerValues.dateNaissance }) ? (
                    <p className="sm:col-span-2 text-xs font-semibold text-error" role="alert">
                      Âge minimum 17 ans, ou cochez fonctionnaire / employé si vous relevez de cette catégorie.
                    </p>
                  ) : null}
                  {step2Hint ? (
                    <p className="sm:col-span-2 text-xs font-semibold text-error" role="alert">
                      {step2Hint}
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <AuthIconInput
                    label="Code parrain"
                    icon="handshake"
                    className="sm:col-span-2 uppercase"
                    value={registerValues.codeParrain}
                    onChange={(e) =>
                      setRegisterValues((c) => ({
                        ...c,
                        codeParrain: e.target.value.toUpperCase().replace(/\s/g, '').slice(0, 24),
                      }))
                    }
                    placeholder="Ex. JOSUE342 (optionnel)"
                    maxLength={24}
                    autoComplete="off"
                  />
                  <AuthPasswordInput
                    label="Mot de passe *"
                    autoComplete="new-password"
                    value={registerValues.motdepasse}
                    onChange={(e) => setRegisterValues((c) => ({ ...c, motdepasse: e.target.value }))}
                    placeholder="8 car. min."
                  />
                  <AuthPasswordInput
                    label="Confirmer *"
                    autoComplete="new-password"
                    value={registerValues.confirmMotdepasse}
                    onChange={(e) => setRegisterValues((c) => ({ ...c, confirmMotdepasse: e.target.value }))}
                    placeholder="Répéter"
                  />
                  <label className="mt-2 flex items-start gap-2 sm:col-span-2 sm:mt-2.5">
                    <input
                      type="checkbox"
                      className="academia-checkbox mt-0.5"
                      checked={registerValues.acceptationConditions}
                      onChange={(e) =>
                        setRegisterValues((c) => ({ ...c, acceptationConditions: e.target.checked }))
                      }
                    />
                    <span className="text-xs text-on-surface-variant sm:text-sm">
                      J&apos;accepte les{' '}
                      <Link
                        to="/legal/cgu"
                        className="font-semibold text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        conditions d&apos;utilisation
                      </Link>{' '}
                      et la{' '}
                      <Link
                        to="/legal/confidentialite"
                        className="font-semibold text-primary hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        politique de confidentialité
                      </Link>
                      .
                    </span>
                  </label>
                </>
              )}
            </div>

            {isRegister && registerStep === 1 ? (
              <button
                type="button"
                disabled={!step1Complete}
                onClick={() => {
                  setErrorMessage('');
                  setRegisterStep(2);
                }}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-public-btn primary-gradient px-4 py-2.5 text-sm font-bold text-white shadow-md hover:opacity-95 disabled:opacity-60"
              >
                Suivant
                <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} className="text-white" />
              </button>
            ) : null}

            {isRegister && registerStep === 2 ? (
              <div className="mt-4 flex flex-col gap-2 sm:mt-5 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setErrorMessage('');
                    setRegisterStep(1);
                  }}
                  className="flex flex-1 items-center justify-center rounded-public-btn border border-outline-variant/30 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-black/[0.03]"
                >
                  Retour
                </button>
                <button
                  type="button"
                  disabled={!step2Complete}
                  onClick={() => {
                    setErrorMessage('');
                    setRegisterStep(3);
                  }}
                  className="flex flex-[1.4] items-center justify-center gap-2 rounded-public-btn primary-gradient px-4 py-2.5 text-sm font-bold text-white shadow-md hover:opacity-95 disabled:opacity-60"
                >
                  Suivant
                  <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} className="text-white" />
                </button>
              </div>
            ) : null}

            {isRegister && registerStep === 3 ? (
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setErrorMessage('');
                    setRegisterStep(2);
                  }}
                  className="flex flex-1 items-center justify-center rounded-public-btn border border-outline-variant/30 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-black/[0.03]"
                >
                  Retour
                </button>
                <button
                  type="submit"
                  disabled={loading || googleLoading || !canSubmitRegister}
                  className="flex flex-[1.4] items-center justify-center gap-2 rounded-public-btn primary-gradient px-4 py-2.5 text-sm font-bold text-white shadow-md hover:opacity-95 disabled:opacity-60"
                >
                  {loading ? 'Traitement…' : "S'inscrire"}
                  <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} className="text-white" />
                </button>
              </div>
            ) : null}
          </>
        )}

        {!isRegister || hasOtpPending ? (
          <button
            type="submit"
            disabled={loading || googleLoading}
            className="flex w-full items-center justify-center gap-2 rounded-public-btn primary-gradient px-4 py-2.5 text-sm font-bold text-white shadow-md hover:opacity-95 disabled:opacity-60"
          >
            {loading || googleLoading
              ? 'Traitement…'
              : hasOtpPending
                ? 'Valider le code'
                : 'Se connecter'}
            <LucideSymbolIcon name="arrow_forward" tone="inherit" size={18} className="text-white" />
          </button>
        ) : null}
      </form>
    </>
  );
}
